import type { BreadcrumbProps } from '@magicschool/spellbook/components/Breadcrumb';
import type { LinkProps } from '@magicschool/spellbook/components/Link';
import { Link } from '@magicschool/spellbook/components/Link';
import { Slot } from '@magicschool/spellbook/components/Slot';
import type { TextProps } from '@magicschool/spellbook/components/Text';
import { Text } from '@magicschool/spellbook/components/Text';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva } from 'class-variance-authority';

/** Marks a link crumb's inner text, the element that ellipsizes, for the overflow measurement to find. */
export const BREADCRUMB_LINK_TEXT_CLASS = 'breadcrumb-link-text';

const breadcrumbLinkVariants = cva('min-w-0 max-w-full truncate', {
  variants: {
    variant: {
      default: ['[&>.link]:not-hover:text-default-text-soft', '[&>.link]:border-b-0'],
    },
  },
});

export const BreadcrumbLinkSlot = Slot<LinkProps & { variant: BreadcrumbProps['variant'] }>({
  displayName: 'Breadcrumb.Link',
  preventOverride: true,
  renderDefault: ({ variant, className, linkContentClassName, ...props }) => {
    return (
      <Link
        data-spellbook-name="breadcrumb-link"
        {...props}
        className={cn(breadcrumbLinkVariants({ variant }), className)}
        // The anchor is a flex container, where text-overflow does nothing, so its inner text ellipsizes instead.
        linkContentClassName={cn('min-w-0 truncate', BREADCRUMB_LINK_TEXT_CLASS, linkContentClassName)}
      />
    );
  },
});

export const BreadcrumbPageSlot = Slot<TextProps & { variant: BreadcrumbProps['variant'] }>({
  displayName: 'Breadcrumb.Page',
  preventOverride: true,
  renderDefault: ({ variant = 'default', className, ...props }) => {
    return (
      <Text
        variant="label-1"
        element="span"
        color={variant === 'default' ? 'default-strong' : 'default'}
        data-spellbook-name="breadcrumb-page"
        role="link"
        aria-disabled="true"
        aria-current="page"
        className={cn('truncate min-w-0', className)}
        {...props}
      />
    );
  },
});
